// ============================================================
// Variant colours on the listing cards
// ============================================================
// Variant templates hide their variants in the listing, so their
// available colours were only visible after opening the item. An
// attribute option of type "Color" carries a hex value
// (Item Attribute Value.custom_color), which is enough for a dot —
// the card then shows at a glance which colours exist for the
// template.
//
// The listing API carries no attribute data per item, so the values
// are fetched once per template from the variant selector endpoint
// and cached (in memory + sessionStorage).
//
// The card builders call webshop.variant_colours.render_result(items)
// after every listing render (initial load, filter change, load-more,
// show-all).
(() => {
	const CACHE_KEY = "ws_variant_colours_v1";
	const MAX_DOTS = 8;

	const routeToCode = {};	// listing route -> item code of the template
	let cache = {};			// item code -> [{ attribute, type, values, colors }]

	try {
		cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}") || {};
	} catch (e) {
		cache = {};
	}

	function persist() {
		try {
			sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
		} catch (e) {}
	}

	// Plain GET on purpose: keeps the background calls out of the listing
	// request and needs no CSRF token.
	function apiGet(method, params) {
		const query = Object.keys(params)
			.map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(params[key]))
			.join("&");

		return fetch(`/api/method/${method}?${query}`, { credentials: "same-origin" })
			.then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
			.then((payload) => payload.message);
	}

	function load(codes) {
		const missing = (codes || []).filter((code) => code && !cache[code]);

		return Promise.all(
			missing.map((code) =>
				apiGet("webshop.webshop.variant_selector.utils.get_attributes_and_values", {
					item_code: code,
				})
					.then((data) => {
						cache[code] = (data || []).map((entry) => {
							const details = entry.value_details || {};
							const colors = {};

							(entry.values || []).forEach((value) => {
								const detail = details[value] || {};
								if (detail.color) colors[value] = detail.color;
							});

							return {
								attribute: entry.attribute,
								type: entry.attribute_type || "",
								values: entry.values || [],
								colors: colors,
							};
						});
						return true;
					})
					.catch(() => false)
			)
		).then((results) => {
			persist();
			return results.every((ok) => ok);
		});
	}

	function entryFor(code) {
		const entries = cache[code];
		if (!entries) return null;

		for (let i = 0; i < entries.length; i++) {
			if (entries[i].type === "Color" && Object.keys(entries[i].colors || {}).length) {
				return entries[i];
			}
		}
		return null;
	}

	// Only templates get dots. A variant already stands for one colour, so
	// showing its siblings there would just be misleading.
	function note(items) {
		(items || []).forEach((item) => {
			if (item.route && item.has_variants) routeToCode[item.route] = item.item_code;
		});
	}

	function codeForCard(card) {
		const link = card.querySelector("a[href]");
		if (!link) return null;

		const route = (link.getAttribute("href") || "").replace(/^\//, "").split("?")[0];
		return routeToCode[route] || null;
	}

	function paint(card, entry) {
		const dots = document.createElement("div");
		dots.className = "ws-variant-colours";

		const values = entry.values.filter((value) => entry.colors[value]);
		values.slice(0, MAX_DOTS).forEach((value) => {
			const dot = document.createElement("span");
			dot.className = "ws-variant-colour";
			dot.style.backgroundColor = entry.colors[value];
			dot.title = value;
			dots.appendChild(dot);
		});

		if (values.length > MAX_DOTS) {
			const more = document.createElement("span");
			more.className = "ws-variant-colours-more";
			more.textContent = "+" + (values.length - MAX_DOTS);
			dots.appendChild(more);
		}

		// Same line as the "Ansehen"/Explore button: variant templates show no
		// cart row, so the colours belong next to that button instead of at the
		// very end of the card (the SCSS puts them at the far right, the button
		// stays at the far left).
		const bottom_row = card.querySelector(".ws-bottom-row");
		const anchor = card.querySelector(".product-code") || card.querySelector(".product-category");
		if (bottom_row) {
			bottom_row.insertBefore(dots, bottom_row.firstChild);
		} else if (anchor && anchor.parentNode) {
			anchor.parentNode.insertBefore(dots, anchor.nextSibling);
		} else {
			card.appendChild(dots);
		}
	}

	function render() {
		const cards = document.querySelectorAll(".list-row, #product-listing .card");
		if (!cards.length) return;

		const pending = [];
		Array.prototype.forEach.call(cards, (card) => {
			if (card.querySelector(".ws-variant-colours")) return;	// already painted

			const code = codeForCard(card);
			if (!code) return;

			const entry = entryFor(code);
			if (entry) {
				paint(card, entry);
			} else if (!cache[code]) {
				if (pending.indexOf(code) === -1) pending.push(code);
			}
		});

		if (pending.length) {
			load(pending).then(() => render());	// second pass once the colours are known
		}
	}

	// Called by the listing view after every render.
	function render_result(items) {
		note(items);
		render();
		// Second pass once the app has finished laying the cards out.
		setTimeout(render, 300);
	}

	webshop.variant_colours = { note: note, render: render, render_result: render_result };
})();
